import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import {
  DillPaymentMethod,
  DillTransaction,
  ScheduledPayment,
  StripeCustomerResponse,
  SupplierRegistrationStatusResponse,
} from '@dill/dill-shared';
import {
  createSupplierPaymentMethodExtraReducer,
  createExternalPaymentMethodExtraReducer,
  getMainBuyerPaymentMethodsExtraReducer,
  getBuyerTransactionsExtraReducer,
  payInvoicesExtraReducer,
  getSupplierTransactionsExtraReducer,
  createSupplierPlaidPaymentMethodExtraReducer,
  getSupplierPaymentMethodsExtraReducer,
  supplierWithdrawExtraReducer,
  getWeekPaymentTransactionsLimitationsForMainBuyerExtraReducer,
  getStripeSupplierAccountExtraReducer,
  getStripeConfigExtraReducer,
  getSupplierRegistrationStatusExtraReducer,
  dwollaTransactionLimitNotifyDillExtraReducer,
  getTransactionByIdExtraReducer,
  verifyDwollaMicroTransactionsExtraReducer,
  deletePaymentMethodByMainBuyerExtraReducer,
  getSupplierPaymentsFilesExtraReducer,
  getSupplierPaymentsReportJsonExtraReducer,
  getBuyerTransactionsFilesExtraReducer,
  sendInvoicePaymentReminderEmailExtraReducer,
  sendBulkInvoicePaymentReminderEmailsExtraReducer,
  getSupplierCustomerPaymentMethodsExtraReducer,
  getSupplierPosTransactionsExtraReducer,
  payFixedAmountExtraReducer,
  getPlaidBankStatementsPDFExtraReducer,
  getPlaidBankStatementsExtraReducer,
  getScheduledPaymentsExtraReducer,
  getSupplierScheduledPaymentsExtraReducer,
  createScheduledPaymentExtraReducer,
  updateScheduledPaymentDateExtraReducer,
  cancelScheduledPaymentExtraReducer,
} from '../reducers/paymentsReducers';

export interface PaymentsInitialState {
  error: string | null;
  paymentMethods: DillPaymentMethod[];
  supplierPaymentMethods: DillPaymentMethod[];
  supplierCustomerPaymentMethods: DillPaymentMethod[];
  buyerTransactions: DillTransaction[];
  loadingList: string[];
  supplierTransactions: DillTransaction[];
  supplierPosTransactions: DillTransaction[];
  totalSupplierBalance: string;
  amountToWithraw: string;
  paymentError: string | null;
  weekLimitations: { totalAmountTransacted: number; weekLimit: number } | null;
  stripeCustomer: StripeCustomerResponse | null;
  supplierRegistrationStatus: SupplierRegistrationStatusResponse | null;
  publishableKey: string | null;
  transactionPlaidStatement: {
    historicalBalance: { month: string; amount: number }[];
    name: string;
  }[];
  totalAmountForPendingTransactions: number;
  paymentsReportData: { payments: any[]; invoicePayments: any[] } | null;
  /** Open (SCHEDULED/PROCESSING) future-dated payments for the current main buyer. */
  scheduledPayments: ScheduledPayment[];
  /** The same, but incoming to the current supplier — enriched with the paying customer's name.
   * Kept separate so the two views can't clobber each other when a user is both a buyer and a
   * supplier. */
  supplierScheduledPayments: (ScheduledPayment & { buyerName?: string | null })[];
}

const initialState: PaymentsInitialState = {
  error: null,
  buyerTransactions: [],
  paymentMethods: [],
  supplierCustomerPaymentMethods: [],
  supplierPaymentMethods: [],
  loadingList: [],
  supplierTransactions: [],
  supplierPosTransactions: [],
  totalSupplierBalance: '',
  amountToWithraw: '',
  paymentError: null,
  weekLimitations: null,
  stripeCustomer: null,
  publishableKey: null,
  supplierRegistrationStatus: null,
  transactionPlaidStatement: [],
  totalAmountForPendingTransactions: 0,
  paymentsReportData: null,
  scheduledPayments: [],
  supplierScheduledPayments: [],
};

export const paymentsSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    resetPaymentsRootState: () => initialState,
  },
  extraReducers(builder) {
    createSupplierPaymentMethodExtraReducer(builder);
    createExternalPaymentMethodExtraReducer(builder);
    getMainBuyerPaymentMethodsExtraReducer(builder);
    getBuyerTransactionsExtraReducer(builder);
    payInvoicesExtraReducer(builder);
    getSupplierTransactionsExtraReducer(builder);
    createSupplierPlaidPaymentMethodExtraReducer(builder);
    getSupplierPaymentMethodsExtraReducer(builder);
    supplierWithdrawExtraReducer(builder);
    getWeekPaymentTransactionsLimitationsForMainBuyerExtraReducer(builder);
    getStripeSupplierAccountExtraReducer(builder);
    getStripeConfigExtraReducer(builder);
    getSupplierRegistrationStatusExtraReducer(builder);
    dwollaTransactionLimitNotifyDillExtraReducer(builder);
    getTransactionByIdExtraReducer(builder);
    verifyDwollaMicroTransactionsExtraReducer(builder);
    deletePaymentMethodByMainBuyerExtraReducer(builder);
    getSupplierPaymentsFilesExtraReducer(builder);
    getSupplierPaymentsReportJsonExtraReducer(builder);
    getBuyerTransactionsFilesExtraReducer(builder);
    sendInvoicePaymentReminderEmailExtraReducer(builder);
    sendBulkInvoicePaymentReminderEmailsExtraReducer(builder);
    getSupplierCustomerPaymentMethodsExtraReducer(builder);
    getSupplierPosTransactionsExtraReducer(builder);
    payFixedAmountExtraReducer(builder);
    getPlaidBankStatementsPDFExtraReducer(builder);
    getPlaidBankStatementsExtraReducer(builder);
    getScheduledPaymentsExtraReducer(builder);
    getSupplierScheduledPaymentsExtraReducer(builder);
    createScheduledPaymentExtraReducer(builder);
    updateScheduledPaymentDateExtraReducer(builder);
    cancelScheduledPaymentExtraReducer(builder);
  },
});

export const { resetPaymentsRootState } = paymentsSlice.actions;
